import type { AcquireLockResult } from '@assessments/shared'
import { apiFetch } from '../../services/apiClient.js'
import { ApiError, isAssessmentSubmitted } from '../../services/apiError.js'

// apiFetch always sends content-type: application/json, and Fastify rejects an
// EMPTY body under that content type — so the body-less lock verbs send '{}'.
const lockPath = (organizationSurveyId: string, sectionId: string) =>
  `/assessments/${encodeURIComponent(organizationSurveyId)}/sections/${encodeURIComponent(sectionId)}/lock`

/** A live lock is held by another admin (HTTP 409). The holder is never disclosed. */
export class LockConflictError extends Error {
  constructor() {
    super('This section is being edited by another admin')
    this.name = 'LockConflictError'
  }
}

async function throwFor(res: Response, verb: string): Promise<never> {
  const body: unknown = await res.json().catch(() => undefined)
  const err = new ApiError(res.status, `Failed to ${verb} section lock (${res.status})`, body)
  if (res.status === 409 && !isAssessmentSubmitted(err)) throw new LockConflictError()
  throw err
}

/** Acquire the section's edit lock (idempotent for the current holder). */
export async function acquireSectionLock(
  organizationSurveyId: string,
  sectionId: string,
): Promise<AcquireLockResult> {
  const res = await apiFetch(lockPath(organizationSurveyId, sectionId), {
    method: 'POST',
    body: '{}',
  })
  if (!res.ok) await throwFor(res, 'acquire')
  return (await res.json()) as AcquireLockResult
}

/** Refresh (heartbeat) the held lock; 409 ⇒ the lock was lost/taken over. */
export async function heartbeatSectionLock(
  organizationSurveyId: string,
  sectionId: string,
): Promise<AcquireLockResult> {
  const res = await apiFetch(lockPath(organizationSurveyId, sectionId), {
    method: 'PUT',
    body: '{}',
  })
  if (!res.ok) await throwFor(res, 'refresh')
  return (await res.json()) as AcquireLockResult
}

/** Release the lock. Idempotent server-side; throws so a caller can keep the lock
 * marked held and retry rather than navigating away believing it was freed. */
export async function releaseSectionLock(
  organizationSurveyId: string,
  sectionId: string,
): Promise<void> {
  const res = await apiFetch(lockPath(organizationSurveyId, sectionId), {
    method: 'DELETE',
    body: '{}',
  })
  if (!res.ok) await throwFor(res, 'release')
}

/**
 * Best-effort release when the page is closing (pagehide). `sendBeacon` only sends
 * POST, so this uses fetch keepalive — the supported way to make a DELETE survive
 * page teardown. The TTL remains the backstop when this doesn't arrive.
 */
export function releaseSectionLockOnClose(organizationSurveyId: string, sectionId: string): void {
  void apiFetch(lockPath(organizationSurveyId, sectionId), {
    method: 'DELETE',
    body: '{}',
    keepalive: true,
  }).catch(() => {})
}
